<div class="header">
  <div class="content">
    <div class="contentInner">
      <div class="pageTitle" appReveal>
        <div class="headline">Dash Enterprise</div>
      </div>
    </div>
    <app-enterprise-tab-navigator></app-enterprise-tab-navigator>
  </div>
</div>

<div class="content">
  <div class="contentInner">
    <h1 class="title">Configuring Dash with central management</h1>
    <p class="intro">You can use the Dash Enterprise management dashboard to choose settings and apply them to your Dash
      devices automatically.  This guide explains how to manage and deploy your configurations.</p>

    <h2 class="sectionHeader">How configuration works</h2>
    <p>
      In Dash Enterprise, your device's configuration can be set on both devices individually, and on device groups.
      You can consider a device group as a collection of devices that share the same overall configuration.
    </p>
    <p>
      When you create a device group and apply a configuration to it, then any devices added to that group will
      be configured with the options you chose.  You can create device groups for any grouping of devices (for example
      by location, such as <i>"New Your office"</i> and <i>"San Francisco office"</i>, or by type such as <i>"Devices for conference
      rooms"</i> and <i>"Devices for quiet booths"</i>).
    </p>
    <p>
      You can also add configuration options directly to a device.  To manage your devices efficiently, we recommend
      creating most of your configurations in device groups, and adding just the settings that apply to an individual
      device (such as the calendar to be displayed) to the device configuration.
    </p>

    <p class="tip icon">
      If the same option is configured in both a device group configuration and on a device configuration, the device
      configuration will take priority.  If an option is not configured in either place in the dashboard, then it can be
      set locally on the device.
    </p>

    <h2 class="sectionHeader">The device overview screen</h2>
    <p>
      Open the device groups screen by clicking <i>Device groups</i> in the left hand navigation bar, and
      click on a group to open the group overview.  If you don't have any groups yet, you can
      <a routerLink="/enterprise/dashboard" fragment="groups">create one</a>.  The device group overview has two
      tabs: <i>Devices</i>, and <i>Configuration</i>.
    </p>

    <h3 class="sectionHeader">The devices tab</h3>
    <p>
      The devices tab lists the devices currently in your group.  Each device is listed with the name of the device,
      the name of the calendar it is displaying (if the device is displaying a single calendar), the device's IP address
      on your network, and the date the device was first added to your account.
    </p>

    <app-illustration alt="The 'Devices' tab of a device group in Dash Enterprise" src="/assets/images/dash-enterprise-device-group-devices.png" webpSrc="/assets/images/dash-enterprise-device-group-devices.webp" width="1485" height="888" class="illustration centered"></app-illustration>

    <h4 id="add-device">Adding a new device</h4>
    <app-illustration alt="Activate a new device using a pairing code in Dash Enterprise" src="/assets/images/dash-enterprise-pairing-code.png" webpSrc="/assets/images/dash-enterprise-pairing-code.webp" width="600" height="422" class="illustration"></app-illustration>
    <p>
      You can create two types of device from the web dashboard - <i>Mobile app</i> devices and <i>Web app</i> devices.
      Mobile app devices use the Dash mobile app (available for Apple and Android devices) and are recommended for most
      cases.  If your device doesn't support our mobile app, you can register your device as a web app instead.
      For more information, please see <a routerLink="/enterprise/web-app">our guide</a>.
    </p>
    <p>
      To add a new device to your group, click the <i>New device</i> button, and enter a type and name for your device.  Click <i>Create</i> and you will
      be given a pairing code.  When creating a web device, you will also be given a unique link for your device.
    </p>
    <p>For mobile app devices, open Dash's settings and navigate to the <i>Subscription</i>
      section, tap <i>Enterprise license</i> and enter the pairing code.
    </p>
    <p>For web devices, open the provided link on your
      device, and then enter the pairing code.
    </p>
    <p class="tip icon single-line">If you close the pairing code dialog before you activate your device, you can
      find all the details in the devices table in your group.
    </p>

    <h4>Adding an existing device</h4>
    <p>
      If your device is already registered to your account, you can add it to the group from the <i>All Devices</i> section of the
      dashboard.  Find the device you would like to add by using the filter box, and select it by clicking on the device
      in the table or using the checkbox on the left hand side of the table.  Click <i>Set group</i> and then choose your
      group in the dropdown.  You can add multiple devices to a group at the same time by selecting more than one device
      in the table before clicking <i>Set group</i>.
    </p>

    <h4>Editing a device's configuration</h4>
    <p>
      To edit a device's configuration, select a single device in the group and click <i>Edit configuration</i>.  Adding
      configuration options to a device works the same way as for groups.  Please see below for details.
    </p>
    <p class="tip icon single-line">
      If you deactivate a device or remove it from the dashboard, its configuration will also be erased.  We recommend using device groups to store most configuration options, as they will persist even if the devices in the group are deactivated.
    </p>

    <h4>Removing a device</h4>
    <p>
      To remove a device from the group, select the device(s) to remove in the group overview and click <i>Remove from group</i>.
      The devices will be removed from the group, but will remain active in your account.  The settings configured in the
      group will no longer be applied to this device.
    </p>
    <p>
      You can add the device(s) to another group from the devices overview screen (see the section on adding an existing device above).
    </p>

    <h2 class="sectionHeader">The configuration tab</h2>
    <p>
      The configuration tab lists the configuration options that you have applied to the device group.  The options shown
      will be applied to all devices in the group as soon as you save the configuration.
    </p>
    <app-illustration alt="The 'Configuration' tab of a device group in Dash Enterprise" src="/assets/images/dash-enterprise-device-configuration.png" webpSrc="/assets/images/dash-enterprise-device-configuration.webp" width="2970" height="1776" class="illustration centered"></app-illustration>
    <h4>Adding configuration values</h4>
    <p>
      Add an option that you would like to centrally control by typing the name of the option
      into the <i>Add option</i> text box.  A list will appear showing all options that match the name you entered.  Click
      on the option you would like to add, and it will be added to your configuration.  Information about each option and
      the values you can set can be found in our <a routerLink="/enterprise/configuration-options">configuration options guide</a>.
    </p>
    <p class="tip icon">
      The device group is the best place to store configuration options that apply to multiple devices (such as the connection to your Microsoft 365 or Google Workspace account,
      branding colorschemes and images, and your general display preferences). <br><br> Configuration options not shared between devices, or
      shared between a small number of devices (such as which room calendar to display) can be set in the device's
      configuration or locally on the device to avoid having to create a large number of device groups.
    </p>
    <h4>Removing configuration values</h4>
    <p>
      To remove a configuration option, click the trash icon in its top right corner.  If the configuration option had
      previously been applied to one or more Dash devices in the group, app devices will keep the value until you set it to
      something else (either directly on the device, or by adding the configuration option to the group again).  Web
      devices will reset the setting to its default value.
    </p>
    <h4>Copying configuration from an existing group</h4>
    <app-illustration alt="Copying configuration from an existing device group in Dash Enterprise" src="/assets/images/dash-enterprise-copy-existing-options.png" webpSrc="/assets/images/dash-enterprise-copy-existing-options.webp" width="629" height="206" class="illustration"></app-illustration>
    <p>
      To make it easier to create several similarly configured groups, it is possible to copy configuration options from
      an existing group.  If your group does not have any configuration options, you will see a button labeled
      <i>Copy from an existing group</i>.
    </p>
    <p>Click the button and select the group to copy from.  The options and values
      from the chosen group will be copied. Any changes made to the configuration on the new group will not affect the configuration
      of the group the options were copied from.
    </p>
    <p class="tip icon single-line"><span>For a list of configuration options and instructions on how to configure them correctly, please see our guide
      on <a routerLink="/enterprise/configuration-options">Configuration options</a>.</span>
    </p>
    <h2 class="sectionHeader">Configuration realtime preview</h2>
    <app-illustration alt="Live preview of your device configuration in Dash Enterprise" src="/assets/images/dash-enterprise-configuration-preview.png" webpSrc="/assets/images/dash-enterprise-configuration-preview.webp" width="2970" height="1776" class="illustration"></app-illustration>
    <p>
      Click the <i>Preview</i> button below the configuration options to open a realtime preview.  The preview shows you
      what your currently configured options will look like on a Dash device, and shows the effect of all options except
      those related to calendars (i.e. except <i>Calendar provider</i>, <i>Calendar</i>, <i>Calendars</i>, <i>Rename
      calendars</i>, and <i>Rooms to check</i>).  The preview is preconfigured as an organization with four meeting
      rooms and ten staff members.
    </p>
    <p>
      The preview is fully interactive, so you can test features such as room booking and preview your branding
      and colorscheme, before saving the configuration and deploying to your devices.
    </p>
  </div>
</div>
